import { useEffect } from 'react';
import { createAuthClient } from 'better-auth/react';
import { getFrontendEnvVariables } from '@mls-wallet/env';
import { magicLinkClient } from 'better-auth/client/plugins';
import { useSWRConfig } from 'swr';
import { setOrgCookie } from '@/lib/api/orgCookie';
import type { SignupAuthorizationInput } from '@mls-wallet/shared-types';

const env = getFrontendEnvVariables();

const auth = createAuthClient({
  baseURL: env.DEN_API_URL,
  basePath: '/api/auth',
  fetchOptions: { credentials: 'include' },
  plugins: [magicLinkClient()],
});

// Wrapper to provide a stable, app-specific API surface (and typed plugin methods)
export function useAuthClient() {
  const { data, error, isPending, refetch } = auth.useSession();
  const { cache, mutate: globalMutate } = useSWRConfig();

  // Re-validate session when the browser tab becomes active
  // This ensures users are logged out if their session was invalidated
  // (e.g., password reset from another device)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refetch();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refetch]);

  const signIn = {
    emailAndPassword: async (args: { email: string; password: string }) =>
      await auth.signIn.email(args),
    magicLink: async (args: { email: string }) =>
      await auth.signIn.magicLink(args),
  };

  const signUp = {
    emailAsInvitee: async (args: {
      email: string;
      password: string;
      name: string;
      signupToken: string;
    }) => {
      const { signupToken, ...rest } = args;
      const callbackURL = getCallbackUrl({
        path: '/organization-redirect',
        query: { signupToken },
      });
      const authorization: SignupAuthorizationInput = {
        email: rest.email,
        signupToken,
      };
      return await auth.signUp.email(
        { ...rest, callbackURL },
        { body: authorization }
      );
    },
    // Wrapper that accepts context and handles callback URL creation/storage
    emailAsOrgCreator: async (args: {
      email: string;
      password: string;
      name: string;
      orgCreateToken: string;
    }) => {
      const { orgCreateToken, ...rest } = args;
      const callbackURL = getCallbackUrl({
        path: '/organization-redirect',
        query: { orgCreateToken },
      });
      const authorization: SignupAuthorizationInput = {
        email: rest.email,
        orgCreateToken,
      };
      return await auth.signUp.email(
        { ...rest, callbackURL },
        { body: authorization }
      );
    },
  };

  const signOut = async () => {
    await auth.signOut({});
    // Clear the organization cookie
    setOrgCookie(null);
    // Wipe the entire SWR cache so no stale data/errors from the previous
    // session leak into the next sign-in.
    // 1) globalMutate notifies any still-mounted hooks to drop their data.
    // 2) cache.delete removes the raw entries (including error/validation
    //    metadata that globalMutate alone leaves behind).
    await globalMutate(() => true, undefined, { revalidate: false });
    for (const key of [...cache.keys()]) {
      cache.delete(key);
    }
  };

  const sessionData = data?.session;
  const rawName = data?.user?.name ?? '';
  const formattedName = rawName.replace(',', ' ').replace(/\s+/g, ' ').trim();

  return {
    session: { data: sessionData, error, isPending, refetch },
    user: data?.user
      ? {
          id: data.user.id,
          name: formattedName,
          email: data.user.email,
        }
      : null,
    signIn,
    signUp,
    signOut,
  };
}

function getCallbackUrl(params: {
  path: string; // e.g. '/create-organization'
  query?: Record<string, string | undefined | null>;
}) {
  const env = getFrontendEnvVariables();
  const url = new URL(`${env.UI_WEB_URL}${params.path}`);
  const query = params.query ?? {};
  for (const [key, value] of Object.entries(query)) {
    if (typeof value === 'string' && value.length > 0) {
      url.searchParams.set(key, value);
    }
  }
  return url.toString();
}
